import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { useQuestionsByDimension } from "@/hooks/questions-by-dimension.hook";
import { useQuizById } from "@/hooks/quiz-by-id.hook";
import { usePrevious } from "@/lib/previous.hook";
import { cn, getMessageFromError } from "@/lib/utils";
import { QuestionsByDimensionResponse } from "@/requests/questionnaries/questions-by-dimension.request";
import { DimensionId } from "@/types/questionnaries";
import { Trash } from "iconsax-react";
import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { reevaluationSchema } from "./reevaluation.schema";

type QuestionFieldsetProps = {
  index: number;
  quizId: string;
};

const findQuestionPosition = (
  data: QuestionsByDimensionResponse,
  questionId: string,
) => {
  for (let topicIndex = 0; topicIndex < data.length; topicIndex++) {
    const questions = data[topicIndex].questions;
    for (
      let questionIndex = 0;
      questionIndex < questions.length;
      questionIndex++
    ) {
      if (questions[questionIndex].id === questionId) {
        return {
          topicIndex,
          questionIndex,
        };
      }
    }
  }
  return null;
};

const QuestionFieldset = (props: QuestionFieldsetProps) => {
  const form = useFormContext<z.infer<typeof reevaluationSchema>>();

  const maxCommentLength = 500;

  const selectedDimensionId = useWatch({
    control: form.control,
    name: `questions.${props.index}.dimensionId`,
  });

  const selectedQuestionId = useWatch({
    control: form.control,
    name: `questions.${props.index}.questionId`,
  });

  const prevSelectedDimensionId = usePrevious(selectedDimensionId);

  const { data: userProfile } = useCurrentUserProfile();

  const { data: quiz } = useQuizById({
    companyId: userProfile?.companyId || "",
    applicationQuizId: props.quizId || "",
  });

  // Get all dimensions except from GENERAL_INFORMATION group
  const dimensions = quiz?.dimensionsSummary.filter(
    (dimension) => dimension.group !== "GENERAL_INFORMATION",
  );

  const {
    data: topics,
    isLoading: isQuestionsLoading,
    error: questionsError,
  } = useQuestionsByDimension({
    companyId: userProfile?.companyId || "",
    applicationQuizId: props.quizId || "",
    dimensionId: selectedDimensionId as DimensionId,
  });

  const handleRemoveQuestion = () => {
    form.resetField(`questions.${props.index}`);
  };

  // When the selected dimension changes, reset the questionId field
  useEffect(() => {
    if (
      selectedDimensionId &&
      selectedDimensionId !== prevSelectedDimensionId
    ) {
      form.setValue(`questions.${props.index}.questionId`, "");
    }
  }, [selectedDimensionId]);

  // When the questionId changes, update the questionNumber field
  useEffect(() => {
    const questionPosition =
      selectedQuestionId && topics
        ? findQuestionPosition(topics, selectedQuestionId)
        : null;

    form.setValue(
      `questions.${props.index}.questionNumber`,
      questionPosition
        ? `${questionPosition.topicIndex + 1}.${questionPosition.questionIndex + 1}`
        : "",
    );
  }, [selectedQuestionId]);

  // Show toast when the questionsByDimension query fails
  useEffect(() => {
    if (questionsError) {
      toast.error(getMessageFromError(questionsError));
    }
  }, [questionsError]);

  return (
    <div className="group flex flex-col gap-2">
      <div className="flex items-center">
        <h3 className="grow font-bold text-[#615CB7]">
          Pregunta {props.index + 1}
        </h3>
        <Button
          className="invisible shrink-0 group-hover:visible"
          variant={"link"}
          size={"sm"}
          type="button"
          onClick={handleRemoveQuestion}
        >
          <Trash size={16} />
          Limpiar
        </Button>
      </div>

      <FormField
        control={form.control}
        name={`questions.${props.index}.dimensionId`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Dimensión*</FormLabel>
            <Select
              onValueChange={field.onChange}
              defaultValue={field.value}
              value={field.value ?? ""}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona una dimensión" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {dimensions?.map((dimension) => (
                  <SelectItem
                    key={dimension.dimension}
                    value={dimension.dimension}
                  >
                    {dimension.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name={`questions.${props.index}.questionId`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Pregunta
              <span className={cn("hidden", selectedDimensionId && "inline")}>
                *
              </span>
            </FormLabel>
            <Select
              onValueChange={field.onChange}
              defaultValue={field.value}
              disabled={!topics?.length || isQuestionsLoading}
              value={field.value ?? ""}
            >
              <FormControl>
                <SelectTrigger className="text-left">
                  <SelectValue placeholder="Selecciona una pregunta" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {topics?.map((topic, topicIndex) => (
                  <SelectGroup key={topic.topic}>
                    <SelectLabel>
                      {topicIndex + 1}. {topic.topicName}
                    </SelectLabel>
                    {topic.questions.map((question, questionIndex) => (
                      <SelectItem key={question.id} value={question.id}>
                        {topicIndex + 1}.{questionIndex + 1}. {question.title}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name={`questions.${props.index}.comment`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Comentario
              <span className={cn("hidden", selectedDimensionId && "inline")}>
                *
              </span>
            </FormLabel>
            <FormControl>
              <Textarea
                placeholder="Escriba aquí sus comentarios"
                minLength={1}
                maxLength={maxCommentLength}
                disabled={!topics?.length || isQuestionsLoading}
                {...field}
              />
            </FormControl>
            <div className="flex flex-row-reverse gap-2">
              <FormDescription
                className={cn(
                  "shrink-0",
                  field.value.length >= maxCommentLength && "font-bold",
                )}
              >
                {field.value.length}/{maxCommentLength}
              </FormDescription>
              <FormMessage className="grow" />
            </div>
          </FormItem>
        )}
      />
    </div>
  );
};

export default QuestionFieldset;
